import React, {FC, memo, MutableRefObject, useCallback, useMemo, useRef, useState} from "react"
import {Button, Checkbox, Popup, TableCell, TableRow} from "semantic-ui-react"
import {I18nIndividual as GedcomIndividual, Sex} from "../../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../../model/gedcom-family"
import {CreatorLocationCell} from "../creator-location-cell"
import {SelectCell, useRelatedIndividualOptions} from "../creator-select-cell"
import {createDateCell} from "../creator-date-cell"
import {createTextCell} from "../creator-text-cell"
import {useLocation} from "react-router"
import {lowestId, removeRow, setFamilyBindings, shiftIdsAfter, shortenId} from "../../utils/creator-utils"
import {FormattedMessage} from "react-intl"
import {ConfirmRemove} from "../confirm-remove"
import {EventHandlers} from "../creator"
import {useRelatedDeps} from "../creator-related-deps"


interface FamilyRowProps {
    row: GedcomFamily
    rowIndex: number
    lastId: MutableRefObject<number>
    data: GedcomFamily[]
    individualsData: GedcomIndividual[]
    childrenMap: Record<string, GedcomIndividual[]>
    spousesMap: Record<string, GedcomFamily[]>
    fullNames: Record<string, string>
    eventHandlers: EventHandlers
}

export const CreatorFamRow: FC<FamilyRowProps> = memo(({
    row, rowIndex, lastId, data, individualsData,
    childrenMap, spousesMap, fullNames,
    eventHandlers
}) => {
    const location = useLocation()
    const from = location.state?.from ?? null
    const rowVersion = useRef(0)
    const [rowToRemove, setRowToRemove] = useState<GedcomFamily | undefined>()
    const [removeAnchor, setRemoveAnchor] = useState<HTMLElement | undefined>()

/* ------------------------------------------------------------------------------------------------------------------ */
/* EVENT HANDLERS                                                                                                     */
/* ------------------------------------------------------------------------------------------------------------------ */

    const handleEnabledChange = useCallback((checked: boolean) =>
        eventHandlers.onChange!(rowIndex, "enabled", checked),
    [rowIndex, eventHandlers.onChange])

    const onSpouseChange = useCallback((rowIndex: number, field: keyof GedcomIndividual | keyof GedcomFamily, value: any) => {
        eventHandlers.onChange!(rowIndex, field, value)  // super()
        setFamilyBindings(row, individualsData, field, value)
    }, [row, individualsData, eventHandlers])

    const handleMarriagePlaceChange = useCallback((marriagePlace?: string) =>
        eventHandlers.onChange!(rowIndex, "marriagePlace", marriagePlace),
    [rowIndex, eventHandlers.onChange])

    const handleConfirmRemove = useCallback((remove: GedcomFamily) => {
        const rowIndex = data.findIndex(row => row.id === remove.id)
        if (rowIndex !== -1) {
            removeRow(rowIndex, lastId, eventHandlers.setFamiliesData!, GedcomFamily)
            eventHandlers.setUnsavedChanges!(true)
            if (!from) shiftIdsAfter(GedcomFamily, rowIndex, rowVersion, eventHandlers.setFamiliesData!)
        }
        setRowToRemove(undefined)
    }, [from, data, lastId, eventHandlers.setFamiliesData, eventHandlers.setUnsavedChanges])

/* ------------------------------------------------------------------------------------------------------------------ */
/* MEMOIZED SUBCOMPONENTS                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */


    const { husbandName, wifeName } = useRelatedDeps(individualsData, data)

    const familyTooltip = useMemo(() => (
        <p>
            <FormattedMessage id="creator.tooltip.family.names" values={{
                husbandName: <span className="tooltip male">{husbandName(row)}</span>,
                wifeName: <span className="tooltip female">{wifeName(row)}</span>
            }}
            />&nbsp;
            <FormattedMessage id="creator.tooltip.family.children" values={{
                nChildren: <span style={{ fontWeight: "bold" }}>{row.children.size}</span>
            }}/>
        </p>
    ), [row, husbandName, wifeName])

    const husbandOptions = useRelatedIndividualOptions(individualsData, null, Sex.M, fullNames)
    const wifeOptions = useRelatedIndividualOptions(individualsData, null, Sex.F, fullNames)

/* ------------------------------------------------------------------------------------------------------------------ */
/* ROW COLUMNS                                                                                                        */
/* ------------------------------------------------------------------------------------------------------------------ */

    return (
        // Key by row version so key changes (like bumpIdsAfter) remount the row
        <TableRow id={`famRow-${row.id}`} key={`${row.id}-${rowVersion.current}`} className={row.enabled ? "" : "disabled-row"}>

            {/* Active */}
            <TableCell className="enabled-column">
                <Checkbox
                    toggle
                    checked={row.enabled}
                    onChange={(_, data) => handleEnabledChange(data.checked ?? false)}
                    style={{ marginLeft: "10px", marginRight: "10px" }}
                />
            </TableCell>

            {/* Family ID */}
            {row.husbandId && row.wifeId ? <Popup
                    flowing
                    hoverable
                    inverted
                    size="large"
                    wide="very"
                    position="top right"
                    offset={[15, -10]}
                    content={familyTooltip}
                    trigger={
                        <TableCell
                            className={`id padded ${row.enabled ? "fam-id-active" : "fam-id-inactive"}`}
                            data-value={shortenId(row.id)}
                            onClick={() => {
                                const targetId = lowestId(row.husbandId!, row.wifeId!)
                                if (targetId) eventHandlers.switchTab?.(`indiRow-${targetId}`)
                            }}
                            style={{ cursor: "pointer" }}
                        />
                    }
                /> : <TableCell className="id padded" data-value={shortenId(row.id)} style={{ cursor: "default" }}/>
            }

            {/* Husband ID */}
            <SelectCell
                field="husbandId"
                rowIndex={rowIndex}
                options={husbandOptions}
                placeholder=""
                onChange={onSpouseChange}
                value={row.husbandId}
                dropdownClassName={`parents-dropdown ${row.husbandId ? "male-bg" : ""}`}
            />
            {/* Wife ID */}
            <SelectCell
                field="wifeId"
                rowIndex={rowIndex}
                options={wifeOptions}
                placeholder=""
                onChange={onSpouseChange}
                value={row.wifeId}
                dropdownClassName={`parents-dropdown ${row.wifeId ? "female-bg" : ""}`}
                cellClassName="no-border-left"
            />

            {/* Marriage date */}
            {createDateCell("marriageDate", rowIndex, eventHandlers.onChange!, row.marriageDate, "date-column")}

            {/* Marriage place */}
            <TableCell className="marriagePlace">
                <CreatorLocationCell
                    maxLength={100}
                    value={row.marriagePlace ?? undefined}
                    onChange={handleMarriagePlaceChange}
                    inputClassname="marriagePlace"
                    popperClassname="from-start"
                    autocomplete={!from}
                />
            </TableCell>

            {/* Notes */}
            {createTextCell("notes", rowIndex, 500, eventHandlers.onChange!, row.notes, "notes")}

            {/* Remove */}
            <TableCell className="remove-column">
                {/* !row.pristine && */ <Button icon="trash" onClick={(e) => { setRowToRemove(row); setRemoveAnchor(e.currentTarget) }}/>}
            </TableCell>
            {rowToRemove && (
                <Popup
                    open={true}
                    pinned
                    flowing
                    inverted
                    on="click"
                    size="large"
                    position="left center"
                    context={removeAnchor}
                    onClose={() => { setRowToRemove(undefined); setRemoveAnchor(undefined) }}
                    content={
                        <ConfirmRemove
                            rowType={GedcomFamily}
                            row={rowToRemove}
                            individualsData={individualsData}
                            familiesData={data}
                            childrenMap={childrenMap}
                            spousesMap={spousesMap}
                            fullNames={fullNames}
                            onConfirmRemove={() => handleConfirmRemove(rowToRemove)}
                        />
                    }
                />
            )}

        </TableRow>
    )
})